/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import AudioContextManager, {
  HotSwapType,
} from "audioProcessing/AudioContextManager";
import { AssetId, AssetSet } from "types/Asset";
import { Clip, PlaylistClip } from "types/Clip";
import { AudioPlayer, AudioPlayerEventMap } from "./AudioPlayer";
import { TypedEventTarget } from "typescript-event-target";
import { BufferingState, PlayingState, WaitingState } from "types/Player";
import { IAssetUrlCache } from "types/AssetUrlCache";

export class LegacyAudioPlayer
  extends TypedEventTarget<AudioPlayerEventMap>
  implements AudioPlayer
{
  _audioContextManager = new AudioContextManager();
  private _assetsLoadingPromise: Promise<void> | undefined;
  private _onPlaybackCompleteCallback: () => void | undefined;
  private _isWaiting: WaitingState = WaitingState.ready;

  setAssetUrlCache(assetUrlCache: IAssetUrlCache): void {
    // Nothing to do here. We cache everything directly in memory for LegacyAudioPlayer
  }

  setPlaybackCompleteCallback(callback: () => void): void {
    this._onPlaybackCompleteCallback = callback;
  }

  private _bufferingState: BufferingState = BufferingState.ready;
  get bufferingState(): BufferingState {
    return this._bufferingState;
  }

  set bufferingState(bufferingState: BufferingState) {
    this._bufferingState = bufferingState;
    this.dispatchTypedEvent(
      "bufferingStateChanged",
      new CustomEvent("bufferingStateChanged", {
        detail: bufferingState,
      })
    );
  }

  async updateAssets(assets: AssetSet): Promise<void> {
    this.bufferingState = BufferingState.buffering;
    this._assetsLoadingPromise = this._audioContextManager.load(assets);
    await this._assetsLoadingPromise;
    this.bufferingState = BufferingState.ready;
  }

  get playingState(): PlayingState {
    return this._audioContextManager.isPlaying
      ? PlayingState.playing
      : PlayingState.paused;
  }

  async updatePlaylist(playlist: PlaylistClip[]): Promise<void> {
    const hotSwapped = await this._audioContextManager.hotSwapPlaylist(
      playlist
    );
    if (hotSwapped !== null) {
      if (
        hotSwapped === HotSwapType.hotswap &&
        JSON.parse(process.env.IS_TEST ?? "false")
      ) {
        (window as any).hasHotSwappedPlaylists = true;
      }
      return;
    }
    const isPlaying = this._audioContextManager.isPlaying;
    await this.pause();
    await this._audioContextManager.setPlaylist(playlist);
    const seekTo =
      this._audioContextManager.duration < this._audioContextManager.elapsedTime
        ? 0
        : this._audioContextManager.elapsedTime;
    await this.seek(seekTo);
    if (isPlaying && !this._isWaiting) {
      await this.play();
    }
  }

  async play(): Promise<void> {
    if (!this._audioContextManager.playlist.length) {
      return;
    }

    await this._assetsLoadingPromise;
    await this._audioContextManager.play();

    this.dispatchTypedEvent(
      "playingStateChanged",
      new CustomEvent("playingStateChanged", {
        detail: PlayingState.playing,
      })
    );
  }

  async pause(): Promise<void> {
    await this._audioContextManager.pause();

    this.dispatchTypedEvent(
      "playingStateChanged",
      new CustomEvent("playingStateChanged", {
        detail: PlayingState.paused,
      })
    );
  }

  // Whether the player is waiting for some external operation to complete before resuming (e.g. video buffering)
  setWaiting(waitingState: WaitingState): void {
    this._isWaiting = waitingState;
  }

  async seek(seekTime: number): Promise<void> {
    await this._audioContextManager.queuePlaylist(
      seekTime,
      this._onPlaybackFinished
    );
  }

  async reset(): Promise<void> {
    return this._audioContextManager.reset();
  }

  getAssetAudioBuffer(
    assetId: AssetId
  ): Promise<AudioBuffer | null> | undefined {
    return this._audioContextManager.getAssetAudioBuffer(assetId);
  }

  loadLocalAsset(assetId: AssetId, blob: Blob): Promise<AudioBuffer | null> {
    return this._audioContextManager.loadLocalAsset(assetId, blob);
  }

  _onPlaybackFinished = async (): Promise<void> => {
    await this.pause();

    await this.seek(0);

    this.dispatchTypedEvent(
      "playbackComplete",
      new CustomEvent("playbackComplete")
    );

    if (this._onPlaybackCompleteCallback) {
      this._onPlaybackCompleteCallback();
    }
  };

  getForegroundClip(): PlaylistClip | undefined {
    return undefined;
  }

  getCurrentTimeMs(): number {
    return this._audioContextManager.elapsedTime * 1000;
  }
}
